import { DateTime } from "luxon";
import { HistoryEntry } from "mock-api/src/managed-receiving-global/types";
import {
  AppointmentInterval,
  AppointmentIntervalDurations,
} from "src/app/core/constants";
import { ChangesHistory } from "src/app/core/history.model";
import { ODataReference } from "src/app/core/odata-reference.model";
import { formatWeekdays } from "src/app/core/pipes/weekdays.pipe";
import {
  getSiteRouteUrl,
  Site,
  SiteModelReference,
  SiteReference,
} from "src/app/core/sites";
import { TimeOfDay } from "src/app/core/time-of-day.model";
import { Weekdays } from "src/app/core/weekdays.model";
import {
  createPartnerResourceUrl,
  getApiDetailsDecorator,
  getEnumMember,
  isExistent,
  ODataModel,
  parseDateTime,
  parseDuration,
  PartnerResourceModel,
} from "src/utils";
import { BaseDock } from "./base-dock.model";
import {
  DailyDockCapacity,
  DailyDockCapacityException,
} from "./daily-dock-capacity.model";
import { DockGroup } from "./dock-group.model";
import {
  HourlyDockCapacity,
  HourlyDockCapacityException,
} from "./hourly-dock-capacity.model";
import { ScheduleCutoffTimeException } from "./schedule-cutoff-time-exception.model";

export const docksResource = createPartnerResourceUrl("docks");
export const expandedDocksResource = docksResource.modify((resource) =>
  resource
    .addExpandFieldAsCount("doors", (doorsResource) => doorsResource.isActive())
    .addExpandField("capacities")
    .addExpandField("hourlyCapacities")
    .addExpandField("dockCutoffExceptions")
    .withReferenceId(),
);

type ApiModelList = PartnerResourceModel<typeof expandedDocksResource>;
export type ApiExpandedDock = ODataModel<ApiModelList>;

const api = getApiDetailsDecorator<ApiExpandedDock>();

interface DeserializeArgs {
  readonly site: Site;
  readonly groups: readonly DockGroup[];
}

export class Dock extends BaseDock implements DockReference {
  private constructor(args: ClassProperties<Dock>) {
    super(args);
    this.createdDate = args.createdDate;
    this.scheduleCutoffTimeExceptions = args.scheduleCutoffTimeExceptions;
    this.dailyCapacities = args.dailyCapacities;
    this.doorCount = args.doorCount;
    this.history = args.history;
    this.hourlyCapacities = args.hourlyCapacities;
    this.id = args.id;
    this.modifiedDate = args.modifiedDate;
    this.reference = args.reference;
  }

  @api() public readonly createdDate: DateTime;
  @api({ key: "capacities" }) public readonly dailyCapacities: ReadonlyArray<
    DailyDockCapacity | DailyDockCapacityException
  >;
  @api({ key: "doors@odata.count" }) public readonly doorCount: number;
  @api() public readonly history: readonly ChangesHistory[];
  @api() public readonly hourlyCapacities: ReadonlyArray<
    HourlyDockCapacity | HourlyDockCapacityException
  >;
  @api({ key: "dockCutoffExceptions" })
  public readonly scheduleCutoffTimeExceptions: readonly ScheduleCutoffTimeException[];
  @api() public readonly id: DockReference["id"];
  @api() public readonly modifiedDate: DateTime;
  @api({ key: "@odata.id" }) public readonly reference: ODataReference;

  public static deserialize(
    data: ApiExpandedDock,
    { site, groups }: DeserializeArgs,
  ): Dock {
    const appointmentInterval =
      // Get the same object so that the radio input comparison in the dock site
      // settings form matches properly for showing which is checked.
      AppointmentIntervalDurations[
        getEnumMember(
          AppointmentInterval,
          data.appointmentInterval,
          AppointmentInterval.Invalid,
        )
      ] ?? parseDuration(data.appointmentInterval, "minutes");
    const group = groups.find(({ id }) => id === data.dockGroupID);
    if (!group) {
      throw new Error(
        `Could not find dock group for ID "${data.dockGroupID}".`,
      );
    }

    return new Dock({
      address: data.address || null,
      appointmentInterval,
      createdDate: parseDateTime(data.createdDate, site),
      scheduleCutoffTimeExceptions: data.dockCutoffExceptions.map((exception) =>
        ScheduleCutoffTimeException.deserialize(exception, { site }),
      ),
      dailyCapacities: data.capacities.map((capacity) =>
        DailyDockCapacity.deserialize(capacity, { site }),
      ),
      doorCount: data["doors@odata.count"],
      earlyScheduleThreshold: data.earlyScheduleThreshold,
      gatePassMessage: data.gatePassMessage || null,
      group,
      history: ChangesHistory.deserializeList(data.history),
      hourlyCapacities: data.hourlyCapacities.map((capacity) =>
        HourlyDockCapacity.deserialize(capacity, { site }),
      ),
      id: data.id,
      isActive: data.active,
      isDockLevelSettingEnabled: data.enableDockLevelSetting,
      isReservationsInThreshold: data.reservationsInThreshold,
      isReservationSlotsOutsideOfHourlyCapacityTimeWindowPresented:
        data.presentReservationSlotsOutsideHourlyCapacityTimeWindow,
      lateScheduleThreshold: data.lateScheduleThreshold,
      modifiedDate: parseDateTime(data.modifiedDate, site),
      maximumAppointmentDuration: isExistent(data.maxAppointmentDuration)
        ? parseDuration(data.maxAppointmentDuration, "minutes")
        : null,
      minimumAppointmentDuration: isExistent(data.minimumUnloadMinutes)
        ? parseDuration(data.minimumUnloadMinutes, "minutes")
        : null,
      name: data.name,
      reference: new ODataReference(data["@odata.id"]),
      scheduleCutoffTime: data.scheduleCutoffTime
        ? TimeOfDay.deserialize(data.scheduleCutoffTime)
        : null,
      site,
      weekdays: Weekdays.deserialize(data.daysOfWeek),
    });
  }

  public static deserializeList(
    { value }: ApiModelList,
    args: DeserializeArgs,
  ): readonly Dock[] {
    return value.map((x) => Dock.deserialize(x, args));
  }

  public static deserializeDockCapacityHistory(
    history: HistoryEntry[],
  ): readonly ChangesHistory[] {
    return ChangesHistory.deserializeList(history, {
      fieldDefinitions: {
        dayOfWeek: {
          label: "Days",
          formatChangeValue: (value) =>
            value ? formatWeekdays(Weekdays.deserialize(value), "short") : null,
        },
      },
    });
  }

  public getRouteUrl(...childPaths: string[]): string {
    return getDockRouteUrl(this.site, this, ...childPaths);
  }
}

export type DockSettings = Pick<
  Dock,
  | "name"
  | "id"
  | "appointmentInterval"
  | "minimumAppointmentDuration"
  | "maximumAppointmentDuration"
  | "isDockLevelSettingEnabled"
>;

export interface DockReference extends SiteModelReference<"dock"> {}

export function getDockRouteUrl(
  site: SiteReference,
  dock: Pick<Dock, "id">,
  ...childPaths: string[]
): string {
  return getSiteRouteUrl(
    site,
    "settings/docks",
    String(dock.id),
    ...childPaths,
  );
}
